import { Button, Card, Col, Form, Row, Switch, Tag, Tooltip } from "antd";
import {
  InfoIconOutlined,
  NeedHelp,
  StarIcon,
} from "../../../atoms/CustomIcons";
import Header from "../../../atoms/Header";
import DashboardTemplate from "../../../templates/Dashboard";
import UploadPhoto from "../../../atoms/UploadPhoto";
import { useEffect, useState } from "react";
import { InputField, SelectField } from "../../../atoms/FormFields";
import { useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import {
  getCompanyDetails,
  removeCompanyDetailsData,
} from "../../Company/ducks/actions";
import { useLocation } from "react-router-dom";
import { onImageDelete } from "../../MyProfile/ducks/services";
import { SuccessMessage } from "../../../atoms/Toasts/SuccessMessage";
import { ErrorMessage } from "../../../atoms/Toasts/ErrorMessage";
import { timezoneList } from "../../../config/utils";
import dayjs from "dayjs";
import { AxiosError } from "axios";
import { uploadUserImage } from "../Employees/ducks/services";
import { RcFile } from "antd/es/upload";
import { updateCompanyById } from "../../Company/ducks/services";
import { AddressComponent, Place } from "../../Company/ducks/types";
import { AutoCompleteField } from "../../../atoms/FormFields/AutoComplete";

interface FormValues {
  name: string;
  address: string;
  timezone: string;
  company_website: string;
}

const CompanyDetails = () => {
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();
  const url = useLocation();
  const id = url?.pathname.split("/")[3];

  const companyDetailData = useAppSelector(
    (state) => state?.company?.companyDetailData
  );

  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (id) {
      dispatch(getCompanyDetails(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (companyDetailData?.data) {
      setValue("name", companyDetailData?.data?.name);
      setValue("address", companyDetailData?.data?.address);
      setValue("timezone", companyDetailData?.data?.timezone);
      setValue("company_website", companyDetailData?.data?.company_website);
    }
  }, [companyDetailData?.data, setValue]);

  function callMembersAPI() {
    dispatch(getCompanyDetails(id));
  }

  const [googleAddress, setGoogleAddress] = useState<Place | null>(null);
  const onSubmit = (val: FormValues) => {
    setLoading(true);

    let city = "";
    let state = "";
    googleAddress?.address_components?.forEach(
      (component: AddressComponent) => {
        if (component.types.includes("locality")) {
          city = component.long_name;
        }
        if (component.types.includes("administrative_area_level_1")) {
          state = component.short_name;
        }
      }
    );

    const payload = {
      name: val?.name?.trim(),
      address: val?.address,
      city: city !== "" ? city : companyDetailData?.data?.city,
      state: state !== "" ? state : companyDetailData?.data?.state,
      timezone: val?.timezone,
      company_website: val?.company_website,
    };

    updateCompanyById(id, payload)
      .then((res) => {
        if (checklistFile && res && id) {
          uploadUserImage({
            owner_id: id,
            image: checklistFile as File | RcFile,
            image_type: "company_image",
          })
            .then((res) => {
              if (res) {
                setLoading(false);
                SuccessMessage("Changes saved.");
              }
            })
            .catch((e: unknown) => {
              if (e instanceof AxiosError) {
                setLoading(false);
                ErrorMessage(
                  e?.response?.data?.error?.message ?? "Something went wrong"
                );
              } else {
                setLoading(false);
                ErrorMessage("Something went wrong");
              }
            });
        } else {
          setLoading(false);
          SuccessMessage("Changes saved.");
        }
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const [checklistFile, setChecklistFile] = useState<File | null>(null);
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const [previewImage, setPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });
  const [modalPreviewImage, setModalPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });

  const saveUpload = () => {
    setLoading(true);
    setPreviewImage({ ...modalPreviewImage });
    setLoading(false);
    setUploadModal(false);
  };

  useEffect(() => {
    dispatch(removeCompanyDetailsData());
  }, [dispatch]);

  return (
    <DashboardTemplate loading={companyDetailData?.loader || loading}>
      <Header
        breadcrumb={[
          { label: "Company settings", href: `/company-settings/${id}` },
          { label: "Company details" },
        ]}
        title="Company details"
        description="General info and multi-branch features."
        btnText1="Need help?"
        btnIcon1={<NeedHelp />}
      />

      <div className="maxWidth">
        <Row gutter={20} style={{ marginBottom: "20px" }}>
          <Col span={24}>
            <UploadPhoto
              size={86}
              darkBg
              saveUpload={saveUpload}
              imageUploaded={companyDetailData?.data?.image_url}
              loading={loading}
              uploadModal={uploadModal}
              setUploadModal={setUploadModal}
              previewImage={previewImage}
              checklistFile={checklistFile}
              modalPreviewImage={modalPreviewImage}
              setChecklistFile={setChecklistFile}
              setPreviewImage={setPreviewImage}
              setModalPreviewImage={setModalPreviewImage}
              infoTextUploader="We support JPG, PNG, and SVG. 2 MB Max."
              onImageDelete={() => {
                onImageDelete(
                  setLoading,
                  SuccessMessage,
                  callMembersAPI,
                  ErrorMessage,
                  companyDetailData?.data?.image_url
                    ? companyDetailData?.data?.image_id
                    : undefined
                );
              }}
            />
          </Col>
        </Row>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <Row gutter={[12, 41]} className="details_card">
            <Col span={12}>
              <Card>
                <div className="title">
                  <h3> Company info </h3>
                  {companyDetailData?.data?.created_date && (
                    <Tag className="orange_tag">
                      <StarIcon /> Customer since{" "}
                      {dayjs(companyDetailData?.data?.created_date).format(
                        "YYYY"
                      )}
                    </Tag>
                  )}
                </div>

                <InputField
                  fieldName="name"
                  label="Company Name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter company name",
                  }}
                  isRequired
                  rules={{
                    required: "Company name is required.",
                  }}
                  validate={errors.name && "error"}
                  validMessage={errors.name && errors.name.message}
                />

                <AutoCompleteField
                  fieldName="address"
                  label="Headquarters address"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Search address",
                  }}
                  onPlaceChange={(place: Place) =>
                    place?.formatted_address ? setGoogleAddress(place) : null
                  }
                  isRequired
                  rules={{
                    required: "Headquarters address is required.",
                  }}
                  validate={errors.address && "error"}
                  validMessage={errors.address && errors.address.message}
                />

                <SelectField
                  fieldName="timezone"
                  label="Company time zone"
                  control={control}
                  selectOption={timezoneList}
                  isRequired
                  rules={{
                    required: "Company time zone is required.",
                  }}
                  validate={errors.timezone && "error"}
                  validMessage={errors.timezone && errors.timezone.message}
                  tooltipTitle="All users will see the same time, based on this time zone, regardless of their actual location. For example, if an executive travels, they’ll still see the work schedule in this “home” time zone."
                />

                <InputField
                  fieldName="company_website"
                  label="Company website"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter Website",
                  }}
                  tooltipTitle="The website domain will be suggested as the email domain for company employee accounts."
                  rules={{
                    validate: (val: string) => {
                      if (!val) return true;
                      const value = val.trim();
                      const regex =
                        /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.){1,}([a-zA-Z]{2,})(\/[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=]*)?$/;
                      return regex.test(value) || "Enter a valid website";
                    },
                  }}
                  validate={errors.company_website && "error"}
                  validMessage={
                    errors.company_website && errors.company_website.message
                  }
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <div className="title justify_end">
                  <div className="title-content">
                    <h3> Multi-branch features </h3>
                    <Tag className="purple_tag"> Coming soon </Tag>
                  </div>
                  <Tooltip title="Multi-branch management for companies with more than one branch.">
                    <Button type="text" className="info_btn outlined-btn">
                      <InfoIconOutlined />
                    </Button>
                  </Tooltip>
                </div>
                <div className="flex_row">
                  <div>
                    <h5> Branches </h5>
                    <p>
                      Manage multiple branches, fine-tune the system for each
                      branch, and create branch reports.
                    </p>
                  </div>
                  <Switch disabled />
                </div>
                <div className="flex_row">
                  <div>
                    <h5> Regions </h5>
                    <p>
                      Grouping branches by regions, regional management and
                      reporting.
                    </p>
                  </div>
                  <Switch disabled />
                </div>
              </Card>
            </Col>
            <Col span={24}>
              <Card className="card_footer" style={{ border: "unset" }}>
                <Button htmlType="submit" type="primary" className="mint-btn">
                  Save changes
                </Button>
              </Card>
            </Col>
          </Row>
        </Form>
      </div>
    </DashboardTemplate>
  );
};

export default CompanyDetails;
